import { Tab, TabList, Badge, mergeClasses } from '@fluentui/react-components';
import type { SelectTabData, SelectTabEvent } from '@fluentui/react-components';
import { useTranslation } from 'react-i18next';
import type { DashboardTabsProps, DashboardTab } from '@/types/events.types';
import { useDashboardTabsStyles } from './DashboardTabs.styles';

export const DashboardTabs = ({
  activeTab,
  onTabChange,
  draftCount,
  currentCount,
  pastCount,
  figmaVariant = false,
}: DashboardTabsProps) => {
  const styles = useDashboardTabsStyles();
  const { t } = useTranslation('dashboard');

  const handleTabSelect = (_event: SelectTabEvent, data: SelectTabData) => {
    onTabChange(data.value as DashboardTab);
  };

  const badgeClass = figmaVariant ? styles.badgeFigma : styles.badge;
  const badgeAppearance = figmaVariant ? 'filled' : 'filled';

  return (
    <div className={mergeClasses(styles.container, figmaVariant && styles.containerFigma)}>
      <TabList
        selectedValue={activeTab}
        onTabSelect={handleTabSelect}
        appearance={figmaVariant ? 'subtle' : 'transparent'}
        className={figmaVariant ? styles.tabListFigma : undefined}
        aria-label="Dashboard sections"
      >
        <Tab
          value="current"
          aria-label={`${t('tabs.current')} (${currentCount})`}
          className={figmaVariant ? styles.tabFigma : undefined}
        >
          <span className={styles.tabContent}>
            {t('tabs.current')}
            {currentCount > 0 && (
              <Badge
                appearance={badgeAppearance}
                color={figmaVariant ? 'subtle' : 'informative'}
                size="small"
                className={badgeClass}
              >
                {currentCount}
              </Badge>
            )}
          </span>
        </Tab>
        <Tab
          value="past"
          aria-label={`${t('tabs.past')} (${pastCount})`}
          className={figmaVariant ? styles.tabFigma : undefined}
        >
          <span className={styles.tabContent}>
            {t('tabs.past')}
            {pastCount > 0 && (
              <Badge
                appearance={badgeAppearance}
                color="subtle"
                size="small"
                className={badgeClass}
              >
                {pastCount}
              </Badge>
            )}
          </span>
        </Tab>
        <Tab
          value="draft"
          aria-label={`${t('tabs.draft')} (${draftCount})`}
          className={figmaVariant ? styles.tabFigma : undefined}
        >
          <span className={styles.tabContent}>
            {t('tabs.draft')}
            {draftCount > 0 && (
              <Badge
                appearance={badgeAppearance}
                color={figmaVariant ? 'subtle' : 'informative'}
                size="small"
                className={badgeClass}
              >
                {draftCount}
              </Badge>
            )}
          </span>
        </Tab>
      </TabList>
    </div>
  );
};

DashboardTabs.displayName = 'DashboardTabs';
